/**
 * Configuração dos prazos a partir dos quais um procedimento solicitado
 * (cirurgia / exame) é considerado "em atraso" na Caixa de Pendências.
 *
 * Persistido em `clinics/{clinicId}/settings/overdueProcedures`. O default
 * é 30 dias para ambos os tipos — caso a clínica não tenha customizado
 * ainda, lemos esses valores em memória sem precisar criar o doc.
 *
 * Não armazenamos o status das prescrições aqui: o painel cruza este
 * config com `PrescriptionService.listPendingProcedures` e calcula o
 * "está em atraso?" em runtime.
 */
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { db } from './config';

export interface OverdueDeadlinesConfig {
  /** Dias após createdAt para cirurgia (SURGERY) entrar como em atraso. */
  surgeryDays: number;
  /** Dias após createdAt para exame (EXAM_REQUEST) entrar como em atraso. */
  examDays: number;
}

export const DEFAULT_OVERDUE_DEADLINES: OverdueDeadlinesConfig = {
  surgeryDays: 30,
  examDays: 30,
};

const MIN_DAYS = 1;
const MAX_DAYS = 365;

function clampDays(value: number, fallback: number): number {
  if (!Number.isFinite(value)) return fallback;
  const int = Math.round(value);
  if (int < MIN_DAYS) return MIN_DAYS;
  if (int > MAX_DAYS) return MAX_DAYS;
  return int;
}

let cached: { clinicId: string; value: OverdueDeadlinesConfig } | null = null;

export const OverdueDeadlinesService = {
  async load(clinicId: string): Promise<OverdueDeadlinesConfig> {
    if (cached && cached.clinicId === clinicId) return cached.value;
    try {
      const snap = await getDoc(doc(db, 'clinics', clinicId, 'settings', 'overdueProcedures'));
      if (snap.exists()) {
        const raw = snap.data() as Partial<OverdueDeadlinesConfig>;
        const value: OverdueDeadlinesConfig = {
          surgeryDays: clampDays(Number(raw.surgeryDays), DEFAULT_OVERDUE_DEADLINES.surgeryDays),
          examDays: clampDays(Number(raw.examDays), DEFAULT_OVERDUE_DEADLINES.examDays),
        };
        cached = { clinicId, value };
        return value;
      }
    } catch (err) {
      console.error('[overdueDeadlines] load failed:', err);
    }
    cached = { clinicId, value: DEFAULT_OVERDUE_DEADLINES };
    return DEFAULT_OVERDUE_DEADLINES;
  },

  async save(clinicId: string, value: OverdueDeadlinesConfig): Promise<void> {
    const normalized: OverdueDeadlinesConfig = {
      surgeryDays: clampDays(value.surgeryDays, DEFAULT_OVERDUE_DEADLINES.surgeryDays),
      examDays: clampDays(value.examDays, DEFAULT_OVERDUE_DEADLINES.examDays),
    };
    await setDoc(
      doc(db, 'clinics', clinicId, 'settings', 'overdueProcedures'),
      normalized,
      { merge: true },
    );
    cached = { clinicId, value: normalized };
  },

  invalidateCache(): void {
    cached = null;
  },
};

/**
 * Dado um prazo em dias, retorna o cutoff `createdAt` abaixo do qual o
 * documento é considerado em atraso. Ex.: 30 dias = qualquer doc criado há
 * mais de 30 dias está em atraso.
 */
export function cutoffMsFromDays(days: number): number {
  return Date.now() - Math.max(MIN_DAYS, days) * 24 * 60 * 60 * 1000;
}

/** Dias inteiros decorridos desde `createdAt`. */
export function daysSince(createdAtMs: number): number {
  return Math.floor((Date.now() - createdAtMs) / (24 * 60 * 60 * 1000));
}
